import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import Modal from '../../components/Modal';

const STATUS_CONFIG = {
  current: { label: 'Current', color: '#10b981', bg: '#ecfdf5' },
  expiring_soon: { label: 'Expiring Soon', color: '#d97706', bg: '#fffbeb' },
  expired: { label: 'Expired', color: '#ef4444', bg: '#fef2f2' },
  pending: { label: 'Pending', color: '#3b82f6', bg: '#eff6ff' },
  not_applicable: { label: 'N/A', color: '#6b7280', bg: '#f3f4f6' },
};

const ENTITY_TYPES = [
  { value: 'employee', label: 'Employee' },
  { value: 'company', label: 'Company' },
  { value: 'facility', label: 'Facility' },
  { value: 'truck', label: 'Truck' },
];

const EMPTY_FORM = {
  entity_type: 'employee', entity_id: '', category_id: '', title: '', description: '',
  is_required: true, issue_date: '', expiration_date: '', renewal_date: '',
  reminder_days: '30,7,1', assigned_owner_id: '', manual_status: '', notes: '',
};

export default function ComplianceItemsPage() {
  const { can, user } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [entities, setEntities] = useState({ employee: [], company: [], facility: [], truck: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [stats, setStats] = useState(null);

  // Filters
  const [filterEntityType, setFilterEntityType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Bulk add state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({ entity_type: 'employee', entity_ids: [], category_id: '', title: '', description: '', is_required: true, expiration_date: '', reminder_days: '30,7,1' });
  const [bulkSaving, setBulkSaving] = useState(false);

  // Document upload state
  const [showDocModal, setShowDocModal] = useState(null); // compliance_item_id
  const [docFiles, setDocFiles] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [itemDocs, setItemDocs] = useState([]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterEntityType) filters.entity_type = filterEntityType;
      if (filterCategory) filters.category_id = filterCategory;
      if (filterStatus) filters.status = filterStatus;
      const data = await api.getComplianceItems(filters);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load items');
    }
    setLoading(false);
  }, [filterEntityType, filterCategory, filterStatus]);

  const loadMeta = useCallback(async () => {
    try {
      const [cats, usrs, emps, comps, facs] = await Promise.all([
        api.getComplianceCategories(),
        api.getUsers(),
        api.getEmployees(),
        api.getCompanies(),
        api.getFacilities(),
      ]);
      setCategories(Array.isArray(cats) ? cats : []);
      setUsers(Array.isArray(usrs) ? usrs : []);
      setEntities({
        employee: Array.isArray(emps) ? emps : [],
        company: Array.isArray(comps) ? comps : [],
        facility: Array.isArray(facs) ? facs : [],
        truck: [], // loaded separately if needed
      });
      // Try loading trucks
      try {
        const trucks = await api.getTrucks();
        setEntities(prev => ({ ...prev, truck: Array.isArray(trucks) ? trucks : [] }));
      } catch { /* trucks endpoint may not support manage_admin perm */ }
    } catch { /* ignore meta load failures */ }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const data = await api.getComplianceStats();
      setStats(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadStats(); }, [loadStats]);

  function openAdd() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  }

  function openEdit(item) {
    setEditing(item);
    setForm({
      entity_type: item.entity_type,
      entity_id: item.entity_id,
      category_id: item.category_id,
      title: item.title || '',
      description: item.description || '',
      is_required: !!item.is_required,
      issue_date: item.issue_date || '',
      expiration_date: item.expiration_date || '',
      renewal_date: item.renewal_date || '',
      reminder_days: item.reminder_days || '30,7,1',
      assigned_owner_id: item.assigned_owner_id || '',
      manual_status: item.manual_status || '',
      notes: item.notes || '',
    });
    setError('');
    setShowModal(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        entity_id: parseInt(form.entity_id),
        category_id: parseInt(form.category_id),
        assigned_owner_id: form.assigned_owner_id || null,
        manual_status: form.manual_status || null,
      };
      if (editing) {
        await api.updateComplianceItem(editing.id, payload);
        setSuccess('Item updated');
      } else {
        await api.createComplianceItem(payload);
        setSuccess('Item added');
      }
      setShowModal(false);
      loadItems();
      loadStats();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save');
    }
    setSaving(false);
  }

  async function handleDelete(id) {
    try {
      await api.deleteComplianceItem(id);
      setDeleteConfirm(null);
      loadItems();
      loadStats();
      setSuccess('Item removed');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
      setDeleteConfirm(null);
    }
  }

  async function openDocModal(itemId) {
    setShowDocModal(itemId);
    setDocFiles(null);
    try {
      const docs = await api.getComplianceDocuments(itemId);
      setItemDocs(Array.isArray(docs) ? docs : []);
    } catch { setItemDocs([]); }
  }

  async function handleUploadDoc(e) {
    e.preventDefault();
    if (!docFiles || docFiles.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      for (const file of docFiles) formData.append('documents', file);
      await api.uploadComplianceDocument(showDocModal, formData);
      const docs = await api.getComplianceDocuments(showDocModal);
      setItemDocs(Array.isArray(docs) ? docs : []);
      setDocFiles(null);
      loadItems(); // refresh doc counts
      setSuccess('Document uploaded');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Upload failed');
    }
    setUploading(false);
  }

  async function handleDeleteDoc(docId) {
    try {
      await api.deleteComplianceDocument(docId);
      const docs = await api.getComplianceDocuments(showDocModal);
      setItemDocs(Array.isArray(docs) ? docs : []);
      loadItems();
    } catch (err) {
      setError(err.message || 'Failed to delete document');
    }
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  }

  // Entity options for current entity_type selection
  const entityOptions = entities[form.entity_type] || [];

  function getEntityLabel(type, list) {
    if (type === 'employee') return list.map(e => ({ id: e.id, label: `${e.first_name} ${e.last_name}` }));
    if (type === 'company') return list.map(c => ({ id: c.id, label: c.name }));
    if (type === 'facility') return list.map(f => ({ id: f.id, label: f.name }));
    if (type === 'truck') return list.map(t => ({ id: t.id, label: `${t.unit_number} ${t.make || ''} ${t.model || ''}`.trim() }));
    return [];
  }

  function daysUntil(dateStr) {
    if (!dateStr) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(dateStr + 'T00:00:00');
    return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
  }

  function handleBulkChange(e) {
    const { name, value, type, checked } = e.target;
    setBulkForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  }

  function toggleBulkEntity(id) {
    setBulkForm(f => ({
      ...f,
      entity_ids: f.entity_ids.includes(id) ? f.entity_ids.filter(x => x !== id) : [...f.entity_ids, id],
    }));
  }

  function selectAllBulkEntities() {
    const allIds = getEntityLabel(bulkForm.entity_type, entities[bulkForm.entity_type] || []).map(e => e.id);
    setBulkForm(f => ({ ...f, entity_ids: f.entity_ids.length === allIds.length ? [] : allIds }));
  }

  async function handleBulkSave() {
    if (!bulkForm.title.trim() || !bulkForm.category_id || bulkForm.entity_ids.length === 0) return;
    setBulkSaving(true);
    setError('');
    try {
      const result = await api.bulkCreateComplianceItems(bulkForm);
      setShowBulkModal(false);
      setBulkForm({ entity_type: 'employee', entity_ids: [], category_id: '', title: '', description: '', is_required: true, expiration_date: '', reminder_days: '30,7,1' });
      loadItems();
      loadStats();
      setSuccess(result.message || 'Bulk items created');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Bulk create failed');
    }
    setBulkSaving(false);
  }

  function exportCSV() {
    if (items.length === 0) return;
    const headers = ['Title', 'Entity Type', 'Entity', 'Category', 'Status', 'Required', 'Issue Date', 'Expiration Date', 'Days Remaining', 'Renewal Date', 'Owner', 'Documents', 'Notes'];
    const rows = items.map(item => {
      const days = daysUntil(item.expiration_date);
      const sc = STATUS_CONFIG[item.computed_status] || STATUS_CONFIG.current;
      return [
        item.title,
        item.entity_type,
        item.entity_name || '',
        item.category_name || '',
        sc.label,
        item.is_required ? 'Yes' : 'No',
        item.issue_date || '',
        item.expiration_date || '',
        days !== null ? (days < 0 ? `${Math.abs(days)} overdue` : `${days}`) : '',
        item.renewal_date || '',
        item.owner_name || '',
        item.doc_count || 0,
        (item.notes || '').replace(/[\r\n]+/g, ' '),
      ];
    });

    const escape = (val) => {
      const s = String(val ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-items-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!can('manage_admin')) {
    return <div className="page-content"><p>You do not have permission to access this page.</p></div>;
  }

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>Compliance Tracking</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-outline" onClick={exportCSV} disabled={items.length === 0} title="Export filtered items to CSV">
            Export CSV
          </button>
          <button className="btn btn-outline" onClick={() => setShowBulkModal(true)}>Bulk Add</button>
          <button className="btn btn-gold" onClick={openAdd}>+ Add Item</button>
        </div>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          {[
            { label: 'Total Items', value: stats.total, color: '#3b82f6' },
            { label: 'Current', value: stats.current, color: '#10b981' },
            { label: 'Expiring Soon', value: stats.expiring_soon, color: '#d97706' },
            { label: 'Expired', value: stats.expired, color: '#ef4444' },
            { label: 'Pending', value: stats.pending, color: '#3b82f6' },
          ].map(kpi => (
            <div key={kpi.label} style={{
              background: 'white', borderRadius: '12px', padding: '16px 20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)', borderLeft: `4px solid ${kpi.color}`,
            }}>
              <div style={{ fontSize: '24px', fontWeight: 800, color: kpi.color }}>{kpi.value}</div>
              <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginTop: '2px' }}>{kpi.label}</div>
            </div>
          ))}
        </div>
      )}

      {error && !showModal && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select value={filterEntityType} onChange={e => setFilterEntityType(e.target.value)} className="form-input" style={{ maxWidth: '160px' }}>
          <option value="">All Types</option>
          {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="form-input" style={{ maxWidth: '180px' }}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="form-input" style={{ maxWidth: '160px' }}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="loading-spinner">Loading compliance items...</div>
      ) : (
        <div className="table-responsive">
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Entity</th>
                <th>Category</th>
                <th>Expiration</th>
                <th>Status</th>
                <th>Owner</th>
                <th>Docs</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="8" className="text-center">No compliance items found.</td></tr>
              ) : items.map(item => {
                const sc = STATUS_CONFIG[item.computed_status] || STATUS_CONFIG.current;
                const days = daysUntil(item.expiration_date);
                return (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.title}</strong>
                      {item.is_required ? <span style={{ fontSize: '9px', color: '#ef4444', fontWeight: 700, marginLeft: '6px' }}>REQUIRED</span> : null}
                    </td>
                    <td>
                      <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8' }}>{item.entity_type}</span>
                      <br />
                      <span style={{ fontSize: '13px' }}>{item.entity_name}</span>
                    </td>
                    <td>{item.category_name || '\u2014'}</td>
                    <td>
                      {item.expiration_date ? (
                        <>
                          <span>{item.expiration_date}</span>
                          {days !== null && (
                            <div style={{ fontSize: '11px', color: days < 0 ? '#ef4444' : days <= 30 ? '#d97706' : '#64748b', fontWeight: days <= 30 ? 700 : 400 }}>
                              {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Today' : `${days}d remaining`}
                            </div>
                          )}
                        </>
                      ) : '\u2014'}
                    </td>
                    <td>
                      <span style={{ padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 700, background: sc.bg, color: sc.color }}>
                        {sc.label}
                      </span>
                    </td>
                    <td style={{ fontSize: '13px' }}>{item.owner_name || '\u2014'}</td>
                    <td>
                      <button className="btn btn-sm btn-outline" onClick={() => openDocModal(item.id)} style={{ fontSize: '11px', padding: '4px 8px' }}>
                        {item.doc_count || 0} file{item.doc_count !== 1 ? 's' : ''}
                      </button>
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline" onClick={() => openEdit(item)}>Edit</button>
                      {' '}
                      <button className="btn btn-sm btn-danger" onClick={() => setDeleteConfirm(item)}>Del</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <Modal title={editing ? 'Edit Compliance Item' : 'Add Compliance Item'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSave}>
            {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}

            {!editing && (
              <div className="form-grid">
                <div className="form-group">
                  <label>Entity Type *</label>
                  <select name="entity_type" value={form.entity_type} onChange={handleChange} className="form-input">
                    {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Entity *</label>
                  <select name="entity_id" value={form.entity_id} onChange={handleChange} required className="form-input">
                    <option value="">Select...</option>
                    {getEntityLabel(form.entity_type, entityOptions).map(e => (
                      <option key={e.id} value={e.id}>{e.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="form-grid">
              <div className="form-group">
                <label>Title *</label>
                <input name="title" value={form.title} onChange={handleChange} required className="form-input" placeholder="e.g. Driver's License" />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select name="category_id" value={form.category_id} onChange={handleChange} required className="form-input">
                  <option value="">Select category...</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <input name="description" value={form.description} onChange={handleChange} className="form-input" />
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Issue / Effective Date</label>
                <input name="issue_date" type="date" value={form.issue_date} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Expiration Date</label>
                <input name="expiration_date" type="date" value={form.expiration_date} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Renewal Due Date</label>
                <input name="renewal_date" type="date" value={form.renewal_date} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Assigned Owner</label>
                <select name="assigned_owner_id" value={form.assigned_owner_id} onChange={handleChange} className="form-input">
                  <option value="">Unassigned</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>)}
                </select>
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Reminder Days (comma-separated)</label>
                <input name="reminder_days" value={form.reminder_days} onChange={handleChange} className="form-input" placeholder="30,7,1" />
              </div>
              <div className="form-group">
                <label>Manual Status Override</label>
                <select name="manual_status" value={form.manual_status} onChange={handleChange} className="form-input">
                  <option value="">Auto (from dates)</option>
                  <option value="pending">Pending (renewal in progress)</option>
                  <option value="not_applicable">Not Applicable</option>
                </select>
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '24px' }}>
                <input type="checkbox" name="is_required" checked={form.is_required} onChange={handleChange} id="is_required" style={{ width: '16px', height: '16px' }} />
                <label htmlFor="is_required" style={{ margin: 0, textTransform: 'none', fontSize: '14px' }}>Required item</label>
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '12px' }}>
              <label>Notes</label>
              <textarea name="notes" value={form.notes} onChange={handleChange} className="form-input" rows={3} />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-gold" disabled={saving}>
                {saving ? 'Saving...' : (editing ? 'Update Item' : 'Add Item')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Document Modal */}
      {showDocModal && (
        <Modal title="Documents" onClose={() => setShowDocModal(null)}>
          {itemDocs.length > 0 ? (
            <div style={{ marginBottom: '16px' }}>
              {itemDocs.map(doc => (
                <div key={doc.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 12px', borderRadius: '8px', border: '1px solid #edf2f7', marginBottom: '8px',
                }}>
                  <div>
                    <a href={`/uploads/${doc.file_path}`} target="_blank" rel="noreferrer" style={{ fontWeight: 600, fontSize: '13px', color: '#3b82f6' }}>
                      {doc.original_name}
                    </a>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                      {doc.uploaded_by_name || 'Unknown'} &middot; {doc.created_at?.slice(0, 10)}
                      {doc.is_current ? <span style={{ marginLeft: '8px', color: '#10b981', fontWeight: 700 }}>CURRENT</span> : null}
                    </div>
                  </div>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDeleteDoc(doc.id)} style={{ fontSize: '11px', padding: '3px 8px' }}>Remove</button>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '16px 0' }}>No documents uploaded yet.</p>
          )}
          <form onSubmit={handleUploadDoc} style={{ borderTop: '1px solid #edf2f7', paddingTop: '16px' }}>
            <div className="form-group">
              <label>Upload New Document</label>
              <input type="file" multiple accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" onChange={e => setDocFiles(e.target.files)} className="form-input" />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setShowDocModal(null)}>Close</button>
              <button type="submit" className="btn btn-gold" disabled={uploading || !docFiles}>
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <Modal title="Remove Item" onClose={() => setDeleteConfirm(null)}>
          <p>Remove <strong>{deleteConfirm.title}</strong> from compliance tracking?</p>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>Remove</button>
          </div>
        </Modal>
      )}

      {/* Bulk Add Modal */}
      {showBulkModal && (
        <Modal title="Bulk Add Compliance Item" onClose={() => setShowBulkModal(false)} wide>
          {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}

          <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '16px' }}>
            Add the same compliance item to multiple {bulkForm.entity_type}s at once. Duplicates are automatically skipped.
          </p>

          <div className="form-grid">
            <div className="form-group">
              <label>Entity Type</label>
              <select name="entity_type" value={bulkForm.entity_type} onChange={e => { handleBulkChange(e); setBulkForm(f => ({ ...f, entity_ids: [] })); }} className="form-input">
                {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Category *</label>
              <select name="category_id" value={bulkForm.category_id} onChange={handleBulkChange} required className="form-input">
                <option value="">Select category...</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Title *</label>
              <input name="title" value={bulkForm.title} onChange={handleBulkChange} className="form-input" placeholder="e.g. Annual Drug Test" />
            </div>
            <div className="form-group">
              <label>Expiration Date</label>
              <input name="expiration_date" type="date" value={bulkForm.expiration_date} onChange={handleBulkChange} className="form-input" />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Reminder Days</label>
              <input name="reminder_days" value={bulkForm.reminder_days} onChange={handleBulkChange} className="form-input" placeholder="30,7,1" />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '24px' }}>
              <input type="checkbox" name="is_required" checked={bulkForm.is_required} onChange={handleBulkChange} id="bulk_required" />
              <label htmlFor="bulk_required" style={{ margin: 0, textTransform: 'none', fontSize: '14px' }}>Required</label>
            </div>
          </div>

          {/* Entity selection */}
          <div style={{ marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ fontWeight: 700, fontSize: '13px' }}>
                Select {bulkForm.entity_type}s ({bulkForm.entity_ids.length} selected)
              </label>
              <button className="btn btn-sm btn-outline" onClick={selectAllBulkEntities} style={{ fontSize: '11px' }}>
                {bulkForm.entity_ids.length === (entities[bulkForm.entity_type] || []).length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px' }}>
              {getEntityLabel(bulkForm.entity_type, entities[bulkForm.entity_type] || []).map(e => (
                <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 6px', fontSize: '13px', cursor: 'pointer', borderRadius: '4px' }}>
                  <input type="checkbox" checked={bulkForm.entity_ids.includes(e.id)} onChange={() => toggleBulkEntity(e.id)} />
                  {e.label}
                </label>
              ))}
              {(entities[bulkForm.entity_type] || []).length === 0 && (
                <div style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '12px' }}>No {bulkForm.entity_type}s found</div>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setShowBulkModal(false)}>Cancel</button>
            <button className="btn btn-gold" onClick={handleBulkSave} disabled={bulkSaving || !bulkForm.title.trim() || !bulkForm.category_id || bulkForm.entity_ids.length === 0}>
              {bulkSaving ? 'Creating...' : `Add to ${bulkForm.entity_ids.length} ${bulkForm.entity_type}${bulkForm.entity_ids.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
